import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Modal from "../../../Components/Modal";
import { 
  fetchCategories, 
  createCategory, 
  seedDefaultCategories 
} from "../Reducers/actions";

const CategoriesPage = ({ 
  fetchCategories, 
  createCategory, 
  seedDefaultCategories, 
  categories 
}) => {
  const [loaded, setLoaded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [hierarchicalView, setHierarchicalView] = useState(false);
  const [formData, setFormData] = useState({
    naam: '',
    beschrijving: '',
    prioriteit: 'normaal',
    slaUren: 24,
    kleurCode: '#6c757d',
    icoon: '',
    parent: '',
    vereistSpecialisatie: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoaded(false);
      try {
        await fetchCategories(hierarchicalView);
        setLoaded(true);
      } catch (error) {
        console.error('Error loading categories:', error);
        setLoaded(true);
      }
    };

    loadData();
  }, [hierarchicalView, fetchCategories]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSpecialisatieChange = (e) => {
    const value = e.target.value;
    const specialisaties = value.split(',').map(s => s.trim()).filter(s => s);
    setFormData(prev => ({
      ...prev,
      vereistSpecialisatie: specialisaties
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.naam.trim()) {
      newErrors.naam = 'Naam is verplicht';
    }

    if (!formData.beschrijving.trim()) {
      newErrors.beschrijving = 'Beschrijving is verplicht';
    }

    if (formData.slaUren < 1) {
      newErrors.slaUren = 'SLA uren moet minimaal 1 zijn';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Clean form data - remove empty parent field
      const submitData = { ...formData };
      if (submitData.parent === '') {
        delete submitData.parent;
      }
      
      await createCategory(submitData);
      
      // Reset form
      setFormData({
        naam: '',
        beschrijving: '',
        prioriteit: 'normaal',
        slaUren: 24,
        kleurCode: '#6c757d',
        icoon: '',
        parent: '',
        vereistSpecialisatie: []
      });
      setShowAddForm(false);
      
      // Reload categories
      await fetchCategories(hierarchicalView);
    } catch (error) {
      console.error('Error creating category:', error);
      setErrors({ submit: 'Er is een fout opgetreden bij het aanmaken van de categorie' });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedCategories = async () => {
    if (window.confirm('Wilt u standaard categorieën aanmaken? Dit kan alleen als er nog geen categorieën bestaan.')) {
      try {
        await seedDefaultCategories();
        await fetchCategories(hierarchicalView);
      } catch (error) {
        console.error('Error seeding categories:', error);
      }
    }
  };

  const getPriorityColor = (prioriteit) => {
    switch (prioriteit) {
      case 'kritiek': return 'danger';
      case 'hoog': return 'warning';
      case 'normaal': return 'info';
      case 'laag': return 'success';
      default: return 'secondary';
    }
  };

  const renderCategoryCard = (category, isChild = false) => (
    <div key={category._id} className={`col-xl-4 col-lg-6 ${isChild ? 'ms-4' : ''}`}>
      <div className="card h-100">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="d-flex align-items-center">
              {category.icoon && <i className={`${category.icoon} me-2`}></i>}
              <h5 className="card-title mb-0">{category.naam}</h5>
            </div>
            <div className="d-flex gap-1">
              <span className={`badge bg-label-${getPriorityColor(category.prioriteit)}`}>
                {category.prioriteit}
              </span>
              {category.parent && (
                <span className="badge bg-label-secondary">Sub</span>
              )}
            </div>
          </div>
          
          <p className="card-text text-muted mb-3">{category.beschrijving}</p>
          
          <div className="row g-2 mb-3">
            <div className="col-6">
              <small className="text-muted d-block">SLA</small>
              <strong>{category.slaUren}h</strong>
            </div>
            {category.kleurCode && (
              <div className="col-6">
                <small className="text-muted d-block">Kleur</small>
                <div className="d-flex align-items-center">
                  <div 
                    className="rounded me-2" 
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      backgroundColor: category.kleurCode 
                    }}
                  ></div>
                  <code>{category.kleurCode}</code>
                </div>
              </div>
            )}
          </div>

          {category.vereistSpecialisatie && category.vereistSpecialisatie.length > 0 && (
            <div className="mb-3">
              <small className="text-muted d-block mb-1">Vereiste Specialisatie</small>
              <div className="d-flex flex-wrap gap-1">
                {category.vereistSpecialisatie.map((spec, index) => (
                  <span key={index} className="badge bg-label-primary">
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          )}

          {category.parent && (
            <div className="mb-3">
              <small className="text-muted">
                Onder: <strong>{category.parent.naam}</strong>
              </small>
            </div>
          )}
        </div>
        
        <div className="card-footer bg-transparent">
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-sm btn-outline-primary">
              <i className="bx bx-edit"></i>
            </button>
            <button className="btn btn-sm btn-outline-danger">
              <i className="bx bx-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!loaded) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Laden...</span>
            </div>
            <h6 className="text-muted">Categorieën worden geladen...</h6>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/facility">Facility Management</Link>
              </li>
              <li className="breadcrumb-item active">Categorieën</li>
            </ol>
          </nav>
          <h4 className="fw-bold mb-2">
            <i className="bx bx-category-alt me-2"></i>
            Storingen Categorieën
          </h4>
        </div>
        <div className="d-flex gap-2">
          {categories.length === 0 && (
            <button 
              className="btn btn-outline-info"
              onClick={handleSeedCategories}
            >
              <i className="bx bx-data me-2"></i>Standaard Categorieën
            </button>
          )}
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            <i className="bx bx-plus me-2"></i>Nieuwe Categorie
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-1">Weergave Opties</h6>
                  <p className="text-muted mb-0">
                    Kies hoe u de categorieën wilt bekijken
                  </p>
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="hierarchicalToggle"
                    checked={hierarchicalView}
                    onChange={(e) => setHierarchicalView(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="hierarchicalToggle">
                    Hiërarchische weergave
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Category Form Modal */}
      <Modal
        visible={showAddForm}
        onClose={() => {
          setShowAddForm(false);
          setErrors({});
        }}
        width={700}
        height="auto"
      >
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Nieuwe Categorie Toevoegen</h5>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="card-body">
              <div className="row g-3">
                    {/* Naam */}
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="naam">
                        Naam <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.naam ? 'is-invalid' : ''}`}
                        id="naam"
                        name="naam"
                        value={formData.naam}
                        onChange={handleInputChange}
                        placeholder="Naam van de categorie"
                      />
                      {errors.naam && (
                        <div className="invalid-feedback">{errors.naam}</div>
                      )}
                    </div>

                    {/* Prioriteit */}
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="prioriteit">Prioriteit</label>
                      <select
                        className="form-select"
                        id="prioriteit"
                        name="prioriteit"
                        value={formData.prioriteit}
                        onChange={handleInputChange}
                      >
                        <option value="laag">Laag</option>
                        <option value="normaal">Normaal</option>
                        <option value="hoog">Hoog</option>
                        <option value="kritiek">Kritiek</option>
                      </select>
                    </div>

                    {/* Beschrijving */}
                    <div className="col-12">
                      <label className="form-label" htmlFor="beschrijving">
                        Beschrijving <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className={`form-control ${errors.beschrijving ? 'is-invalid' : ''}`}
                        id="beschrijving"
                        name="beschrijving"
                        rows="3"
                        value={formData.beschrijving}
                        onChange={handleInputChange}
                        placeholder="Beschrijving van de categorie..."
                      ></textarea>
                      {errors.beschrijving && (
                        <div className="invalid-feedback">{errors.beschrijving}</div>
                      )}
                    </div>

                    {/* SLA Uren en Kleurcode */}
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="slaUren">SLA Uren</label>
                      <input
                        type="number"
                        className={`form-control ${errors.slaUren ? 'is-invalid' : ''}`}
                        id="slaUren"
                        name="slaUren"
                        min="1"
                        value={formData.slaUren}
                        onChange={handleInputChange}
                      />
                      {errors.slaUren && (
                        <div className="invalid-feedback">{errors.slaUren}</div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label" htmlFor="kleurCode">Kleurcode</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="kleurCode"
                          name="kleurCode"
                          value={formData.kleurCode}
                          onChange={handleInputChange}
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formData.kleurCode}
                          onChange={handleInputChange}
                          name="kleurCode"
                        />
                      </div>
                    </div>

                    {/* Icoon */}
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="icoon">
                        Icoon (FontAwesome/BoxIcons klasse)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="icoon"
                        name="icoon"
                        value={formData.icoon}
                        onChange={handleInputChange}
                        placeholder="bx bx-wrench of fas fa-tools"
                      />
                    </div>

                    {/* Parent Category */}
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="parent">Hoofdcategorie</label>
                      <select
                        className="form-select"
                        id="parent"
                        name="parent"
                        value={formData.parent}
                        onChange={handleInputChange}
                      >
                        <option value="">Geen (hoofdcategorie)</option>
                        {categories.filter(c => !c.parent).map(category => (
                          <option key={category._id} value={category._id}>
                            {category.naam}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Vereiste Specialisatie */}
                    <div className="col-12">
                      <label className="form-label" htmlFor="specialisatie">
                        Vereiste Specialisatie
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="specialisatie"
                        value={formData.vereistSpecialisatie.join(', ')}
                        onChange={handleSpecialisatieChange}
                        placeholder="elektriciteit, loodgieterij, onderhoud (gescheiden door komma's)"
                      />
                      <div className="form-text">
                        Komma gescheiden lijst van specialisaties die vereist zijn voor deze categorie
                      </div>
                    </div>
              </div>

              {/* Error message */}
              {errors.submit && (
                <div className="alert alert-danger mt-3" role="alert">
                  <i className="bx bx-error-circle me-2"></i>
                  {errors.submit}
                </div>
              )}
            </div>
            <div className="card-footer bg-transparent">
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setErrors({});
                  }}
                  disabled={loading}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Laden...</span>
                      </div>
                      Aanmaken...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-plus me-2"></i>
                      Categorie Aanmaken
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Modal>

      {/* Categories Content */}
      {categories.length === 0 ? (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="avatar avatar-xl mb-3">
                  <i className="bx bx-category-alt bx-lg text-muted"></i>
                </div>
                <h4>Geen categorieën gevonden</h4>
                <p className="text-muted mb-4">
                  Er zijn nog geen categorieën aangemaakt. Maak uw eerste categorie aan of gebruik de standaard categorieën.
                </p>
                <div className="d-flex justify-content-center gap-3">
                  <button 
                    className="btn btn-outline-info"
                    onClick={handleSeedCategories}
                  >
                    <i className="bx bx-data me-2"></i>Standaard Categorieën Laden
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setShowAddForm(true)}
                  >
                    <i className="bx bx-plus me-2"></i>Eerste Categorie Aanmaken
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {hierarchicalView ? (
            // Hierarchical view
            categories
              .filter(category => !category.parent)
              .map(category => (
                <React.Fragment key={category._id}>
                  {renderCategoryCard(category)}
                  {/* Render children */}
                  {categories
                    .filter(child => child.parent?._id === category._id)
                    .map(child => renderCategoryCard(child, true))}
                </React.Fragment>
              ))
          ) : (
            // Flat view
            categories.map(category => renderCategoryCard(category))
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  categories: state.facilityManagement?.categories || []
});

const mapDispatchToProps = {
  fetchCategories,
  createCategory,
  seedDefaultCategories
};

export default connect(mapStateToProps, mapDispatchToProps)(CategoriesPage);